/**
 * Storage / security / operations rules of the Apprise VM validation (contract v2).
 * Split from appriseValidation.ts to keep files small; see that file for the overview.
 */
import type { AppriseVmConfig } from '@/types'
import { LOG_ANALYTICS_ID_RE, isValidResourceGroupName } from './azureNames'
import { appriseRegionSupportsZones } from './appriseDefaults'
import { isPasswordMode } from './appriseCredentials'
import { validateTagRecord } from './appriseTags'
import type { AppriseValidationContext, Errors } from './appriseValidation'

export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/
export const KEY_VAULT_NAME_RE = /^[A-Za-z][A-Za-z0-9-]{1,22}[A-Za-z0-9]$/
export const BACKUP_VAULT_NAME_RE = /^[A-Za-z][A-Za-z0-9-]{1,48}[A-Za-z0-9]$/
export const SECRET_NAME_RE = /^[A-Za-z0-9-]{1,127}$/
export const STORAGE_ACCOUNT_NAME_RE = /^[a-z0-9]{3,24}$/
export const USER_ASSIGNED_IDENTITY_ID_RE =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.ManagedIdentity\/userAssignedIdentities\/[^/]+$/i
export const DISK_ENCRYPTION_SET_ID_RE =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Compute\/diskEncryptionSets\/[^/]+$/i

const NOT_SUPPORTED_YET = 'Not supported by the portal yet'
const SECURITY_TYPES = new Set(['standard', 'trusted_launch', 'confidential'])
const BOOT_MODES = new Set(['managed', 'custom', 'disabled'])
const PATCH_MODES = new Set(['manual', 'automatic_by_os', 'automatic_by_platform', 'image_default'])

export function validateStorage(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors, warnings: Errors) {
  const st = c.storage
  const cat = ctx.catalog
  if (!ctx.options.disk_types.includes(st.os_disk_type)) errors['storage.os_disk_type'] = 'Choose a disk type'
  // null = the image's default OS disk size
  if (st.os_disk_gb !== null) {
    if (!Number.isInteger(st.os_disk_gb) || st.os_disk_gb < 30 || st.os_disk_gb > 4095) errors['storage.os_disk_gb'] = 'Between 30 and 4095 GB'
    else if (c.server.os_type === 'windows' && st.os_disk_gb < 127)
      warnings['storage.os_disk_gb'] =
        'Standard Windows Server images need an OS disk of about 127 GB or more — the pipeline preflight will reject a smaller disk (small-disk image variants excepted)'
  }
  if (!cat.overrides_allowed) {
    if (st.os_disk_type !== cat.os_disk_type) errors['storage.os_disk_type'] = 'This SKU does not allow disk overrides'
    if (st.os_disk_gb !== cat.os_disk_gb) errors['storage.os_disk_gb'] = 'This SKU does not allow disk overrides'
  }

  const zonal = appriseRegionSupportsZones(ctx.region, ctx.options)
  if (st.os_disk_type.endsWith('ZRS') && !zonal) warnings['storage.os_disk_type'] = `${ctx.region} has no availability zones — ZRS disks may not be available`

  if (st.data_disk_required) {
    if (st.data_disks.length < 1) errors['storage.data_disks'] = 'Add at least one data disk, or turn data disks off'
    if (st.data_disks.length > 16) errors['storage.data_disks'] = 'At most 16 data disks'
    st.data_disks.forEach((d, i) => {
      if (!Number.isInteger(d.size_gb) || d.size_gb < 4 || d.size_gb > 32767) errors[`storage.data_disks.${i}.size_gb`] = 'Between 4 and 32767 GB'
      if (!ctx.options.disk_types.includes(d.type)) errors[`storage.data_disks.${i}.type`] = 'Choose a disk type'
      if (!ctx.options.disk_caching.includes(d.caching)) errors[`storage.data_disks.${i}.caching`] = 'Choose a caching mode'
    })
  }

  if (st.encryption_at_host)
    warnings['storage.encryption_at_host'] = 'Encryption at host needs the EncryptionAtHost feature registered on the subscription (it is not by default)'
  if (st.disk_encryption_set_id.trim() && !DISK_ENCRYPTION_SET_ID_RE.test(st.disk_encryption_set_id.trim()))
    errors['storage.disk_encryption_set_id'] = 'Use the full disk encryption set resource ID (…/providers/Microsoft.Compute/diskEncryptionSets/…) or leave blank'

  const production = c.server.server_environment === 'production'
  if (st.backup_required) {
    const b = st.backup_policy
    if (!ctx.options.backup_frequencies.includes(b.frequency)) errors['storage.backup_policy.frequency'] = 'Choose a frequency'
    if (!TIME_RE.test(b.time)) errors['storage.backup_policy.time'] = 'Use HH:MM (24-hour)'
    if (!Number.isInteger(b.retention_days) || b.retention_days < 7 || b.retention_days > 9999) errors['storage.backup_policy.retention_days'] = 'Between 7 and 9999 days'
    if (!BACKUP_VAULT_NAME_RE.test(b.vault_name)) errors['storage.backup_policy.vault_name'] = '2–50 letters, digits or hyphens; start with a letter'
    if (b.vault_mode === 'existing') {
      if (!b.vault_resource_group.trim()) errors['storage.backup_policy.vault_resource_group'] = 'Resource group of the existing vault is required'
      else if (!isValidResourceGroupName(b.vault_resource_group)) errors['storage.backup_policy.vault_resource_group'] = 'Invalid resource group name'
    }
  } else if (production) {
    warnings['storage.backup_required'] = 'Backups are disabled for a production server'
  }
}

function validateSecurityType(c: AppriseVmConfig, errors: Errors) {
  const sec = c.security
  if (!SECURITY_TYPES.has(sec.security_type)) {
    errors['security.security_type'] = 'Choose a security type'
  } else if (sec.security_type === 'confidential') {
    errors['security.security_type'] = `Confidential VMs are ${NOT_SUPPORTED_YET.toLowerCase()}`
  } else if (sec.security_type === 'standard' && (sec.trusted_launch || sec.secure_boot || sec.vtpm)) {
    errors['security.security_type'] = 'Standard security turns Trusted Launch, Secure Boot and vTPM off'
  } else if (sec.security_type === 'trusted_launch' && !sec.trusted_launch) {
    errors['security.security_type'] = 'Trusted launch needs Trusted Launch enabled'
  }
}

function validateCredentials(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors) {
  const sec = c.security
  const modes = ['default_password', 'custom_password', 'generate_in_key_vault', 'existing_key_vault_secret']
  if (!modes.includes(sec.credential_mode)) errors['security.credential_mode'] = 'Choose how the admin credential is provided'
  if (sec.credential_mode === 'default_password' && ctx.options.default_password_available !== true)
    errors['security.credential_mode'] = 'No default password is configured on the server — choose a custom password'
  if (isPasswordMode(sec.credential_mode) && (ctx.options.executor?.mode === 'github' || ctx.options.password_credentials_supported === false))
    errors['security.credential_mode'] = 'Passwords are supported only with the local executor — use a Key Vault credential'
  // none is valid for default / custom passwords; generated / existing-secret credentials need a Key Vault
  if (sec.credential_mode === 'generate_in_key_vault' && sec.key_vault_mode === 'none')
    errors['security.key_vault_mode'] = 'Credentials are generated into Key Vault — choose a new or existing Key Vault'
  if (sec.credential_mode === 'existing_key_vault_secret') {
    if (sec.key_vault_mode !== 'existing') errors['security.key_vault_mode'] = 'Using an existing secret requires an existing Key Vault'
    if (!SECRET_NAME_RE.test(sec.credential_secret_name.trim())) errors['security.credential_secret_name'] = 'Name of the existing Key Vault secret (letters, digits, hyphens)'
  }
  if (sec.key_vault_mode !== 'none') {
    if (!KEY_VAULT_NAME_RE.test(sec.key_vault_name) || sec.key_vault_name.includes('--'))
      errors['security.key_vault_name'] = '3–24 letters, digits or hyphens; start with a letter, no consecutive hyphens'
    if (sec.key_vault_mode === 'existing') {
      if (!sec.key_vault_resource_group.trim()) errors['security.key_vault_resource_group'] = 'Resource group of the existing Key Vault is required'
      else if (!isValidResourceGroupName(sec.key_vault_resource_group)) errors['security.key_vault_resource_group'] = 'Invalid resource group name'
    }
  }
}

export function validateSecurity(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors) {
  const sec = c.security
  const cat = ctx.catalog
  validateSecurityType(c, errors)

  if (!ctx.options.identity_modes.includes(sec.identity_mode)) errors['security.identity_mode'] = 'Choose an identity mode'
  else if (!cat.overrides_allowed && sec.identity_mode !== cat.identity_default) errors['security.identity_mode'] = 'This SKU does not allow identity overrides'
  // Mirrors terraform/apprise-vm/main.tf: the Entra ID login extension uses the VM's system-assigned identity.
  if (c.server.aad_login_enabled && sec.identity_mode !== 'system_assigned' && !errors['security.identity_mode'])
    errors['security.identity_mode'] =
      'Entra ID login needs the system-assigned managed identity — enable it or turn Entra ID login off'
  if (sec.identity_mode === 'user_assigned') {
    if (!sec.user_assigned_identity_id.trim()) errors['security.user_assigned_identity_id'] = 'The user-assigned identity resource ID is required'
    else if (!USER_ASSIGNED_IDENTITY_ID_RE.test(sec.user_assigned_identity_id.trim()))
      errors['security.user_assigned_identity_id'] = 'Use the full resource ID (…/providers/Microsoft.ManagedIdentity/userAssignedIdentities/…)'
  }

  validateCredentials(c, ctx, errors)
  if (sec.jit_requested && c.server.admin_access_method !== 'jit') errors['security.jit_requested'] = 'Just-in-time access can only be requested with the JIT admin access method'
}

export function validateOperations(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors) {
  const o = c.operations
  const cat = ctx.catalog
  if (o.monitoring_enabled && o.log_analytics_workspace_id.trim() && !LOG_ANALYTICS_ID_RE.test(o.log_analytics_workspace_id.trim()))
    errors['operations.log_analytics_workspace_id'] =
      'Use the full resource ID (/subscriptions/…/providers/Microsoft.OperationalInsights/workspaces/…) or leave blank to create one'
  if (!cat.overrides_allowed && o.monitoring_enabled !== cat.monitoring_default) errors['operations.monitoring_enabled'] = 'This SKU does not allow monitoring overrides'
  if (o.monitoring_enabled && c.security.identity_mode === 'none')
    errors['operations.monitoring_enabled'] =
      'Monitoring (Azure Monitor agent) needs a managed identity — enable the system-assigned managed identity on the Management tab'

  const boot = o.boot_diagnostics
  if (!BOOT_MODES.has(boot.mode)) errors['operations.boot_diagnostics.mode'] = 'Choose a boot diagnostics option'
  else if (boot.mode === 'custom') {
    if (!STORAGE_ACCOUNT_NAME_RE.test(boot.storage_account.trim())) errors['operations.boot_diagnostics.storage_account'] = 'Choose a storage account'
    if (!boot.storage_account_resource_group.trim()) errors['operations.boot_diagnostics.storage_account_resource_group'] = 'Resource group of the storage account is required'
    else if (!isValidResourceGroupName(boot.storage_account_resource_group)) errors['operations.boot_diagnostics.storage_account_resource_group'] = 'Invalid resource group name'
  }

  if (!PATCH_MODES.has(o.patch_mode)) errors['operations.patch_mode'] = 'Choose a patch orchestration option'
  if (o.recommended_alerts) errors['operations.recommended_alerts'] = `Recommended alert rules: ${NOT_SUPPORTED_YET.toLowerCase()}`
  if (o.os_guest_diagnostics) errors['operations.os_guest_diagnostics'] = `OS guest diagnostics: ${NOT_SUPPORTED_YET.toLowerCase()}`
  if (o.app_health_monitoring) errors['operations.app_health_monitoring'] = `Application health monitoring: ${NOT_SUPPORTED_YET.toLowerCase()}`

  Object.assign(errors, validateTagRecord(o.tags))
}
